import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import type { SubscriptionRequest } from '../../../types';
import { uiBus } from '../../../lib/uiBus';

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  services: ['subscription-services'] as const,
  plans: (serviceId: number) => ['subscription-plans', serviceId] as const,
  byProfile: (profileId: number) => ['subscriptions', 'profile', profileId] as const,
  societyActive: (societyId: number) => ['subscriptions', 'society-active', societyId] as const,
};

export function useSubscriptionServicesQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.services,
    queryFn: () => subscriptionService.getServices(),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

export function usePlansByServiceQuery(serviceId: number | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.plans(serviceId ?? 0),
    queryFn: () => subscriptionService.getPlansByService(serviceId!),
    enabled: !!serviceId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useProfileSubscriptionsQuery(profileId: number | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.byProfile(profileId ?? 0),
    queryFn: () => subscriptionService.getProfileSubscriptions(profileId!),
    enabled: !!profileId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSubscribeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SubscriptionRequest) => subscriptionService.subscribe(req),
    onSuccess: (_, req) => {
      qc.invalidateQueries({ queryKey: subscriptionKeys.byProfile(req.profileId) });
      uiBus.notify('Subscribed successfully', 'success');
    },
  });
}

export function useRenewSubscriptionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, planId }: { subscriptionId: number; planId: number }) =>
      subscriptionService.renew(subscriptionId, planId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subscriptionKeys.all });
      uiBus.notify('Subscription renewed successfully', 'success');
    },
  });
}
